import clsx from 'clsx';
import React, {
  memo, useCallback, useEffect, useRef, useState
} from 'react';
import { AiOutlineLoading } from 'react-icons/ai';
import {
  FiChevronDown,
  FiChevronRight,
  FiDatabase,
  FiFile,
  FiHash,
  FiKey,
  FiType
} from 'react-icons/fi';

import APIGateway from '@/api';
import { Icon } from '@/components/icon/icon';
import { Heading } from '@/components/typography/heading';
import { Contract } from '@/types/dataContracts';
import { parseDataContractYAML } from '@/utils/yamlParser';

import styles from '../explorer.module.scss';
import { TreeNode } from '../types';


type TreeNodeItemProps = {
  node: TreeNode;
  depth: number;
  onToggle: ( nodeId: string ) => void;
  onTableDoubleClick?: ( tableName: string ) => void;
};

const getColumnIcon = ( columnType?: string ) => {
  if ( !columnType ) return FiType;
  const t = columnType.toUpperCase();
  if ( t.includes( 'INT' ) || t.includes( 'NUM' ) || t.includes( 'FLOAT' ) || t.includes( 'DEC' ) ) return FiHash;
  if ( t.includes( 'KEY' ) || t.includes( 'ID' ) ) return FiKey;
  return FiType;
};

const TreeNodeItem = memo( ( { node, depth, onToggle, onTableDoubleClick }: TreeNodeItemProps ) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpandable = node.type === 'source' || node.type === 'contract' || node.type === 'table';

  const handleClick = useCallback( () => {
    if ( isExpandable ) {
      onToggle( node.id );
    }
  }, [ node.id, isExpandable, onToggle ] );

  const handleDoubleClick = useCallback( () => {
    if ( node.type === 'table' && node.metadata?.contractId && node.metadata?.tableName && onTableDoubleClick ) {
      onTableDoubleClick( `${ node.metadata.contractId }__${ node.metadata.tableName }` );
    }
  }, [ node.type, node.metadata, onTableDoubleClick ] );

  const renderIcon = () => {
    switch ( node.type ) {
      case 'source':
        return <FiDatabase className={ styles.treeIcon } />;
      case 'contract':
        return <FiFile className={ styles.treeIcon } />;
      case 'table':
        return <FiDatabase className={ styles.treeIcon } />;
      case 'column': {
        const ColIcon = getColumnIcon( node.metadata?.columnType );
        return <ColIcon className={ styles.treeIcon } />;
      }
    }
  };

  return (
    <>
      <div
        className={ clsx( styles.treeNode, styles[`treeNode_${ node.type }`] ) }
        style={ { paddingLeft: `${ depth * 16 + 8 }px` } }
        data-testid={ `tree-node-${ node.id }` }
        onClick={ handleClick }
        onDoubleClick={ handleDoubleClick }
        onKeyDown={ ( e ) => { if ( isExpandable && ( e.key === 'Enter' || e.key === ' ' ) ) { e.preventDefault(); handleClick(); } } }
        role={ isExpandable ? 'button' : undefined }
        tabIndex={ isExpandable ? 0 : undefined }
        title={ node.label }
      >
        { isExpandable && (
          node.isLoading
            ? <Icon icon={ AiOutlineLoading } className={ styles.treeSpinner } />
            : node.isExpanded
              ? <FiChevronDown className={ styles.treeChevron } />
              : <FiChevronRight className={ styles.treeChevron } />
        ) }
        { !isExpandable && <span className={ styles.treeSpacer } /> }
        { renderIcon() }
        <span className={ styles.treeLabel }>{ node.label }</span>
      </div>
      { node.isExpanded && hasChildren && (
        node.children!.map( child => (
          <TreeNodeItem
            key={ child.id }
            node={ child }
            depth={ depth + 1 }
            onToggle={ onToggle }
            onTableDoubleClick={ onTableDoubleClick }
          />
        ) )
      ) }
    </>
  );
} );

TreeNodeItem.displayName = 'TreeNodeItem';


type Props = {
  contracts: Contract[];
  isLoading: boolean;
  onTableDoubleClick?: ( tableName: string ) => void;
};

function DatabaseExplorer( { contracts, isLoading, onTableDoubleClick }: Props ) {
  const [ treeNodes, setTreeNodes ] = useState<TreeNode[]>( [] );
  const loadingNodesRef = useRef<Set<string>>( new Set() );

  // Build tree from contracts grouped by source_type
  useEffect( () => {
    if ( !contracts || contracts.length === 0 ) {
      setTreeNodes( [] );
      return;
    }

    const sourceGroups: Record<string, Contract[]> = {};
    for ( const c of contracts ) {
      const key = c.source_type || 'Unknown';
      if ( !sourceGroups[key] ) sourceGroups[key] = [];
      sourceGroups[key].push( c );
    }

    const nodes: TreeNode[] = Object.entries( sourceGroups ).map( ( [ sourceType, sourceContracts ] ) => ( {
      id: `source_${ sourceType }`,
      label: `${ sourceType } (${ sourceContracts.length })`,
      type: 'source' as const,
      isExpanded: true,
      metadata: { sourceType },
      children: sourceContracts.map( c => ( {
        id: `contract_${ c.contract_id }`,
        label: `${ c.contract_id } — ${ c.name }`,
        type: 'contract' as const,
        isExpanded: false,
        metadata: { contractId: c.contract_id, sourceType: c.source_type },
        children: []
      } ) )
    } ) );

    setTreeNodes( nodes );
  }, [ contracts ] );

  const updateNode = useCallback( ( nodes: TreeNode[], nodeId: string, updater: ( node: TreeNode ) => TreeNode ): TreeNode[] => {
    return nodes.map( n => {
      if ( n.id === nodeId ) return updater( n );
      if ( n.children ) return { ...n, children: updateNode( n.children, nodeId, updater ) };
      return n;
    } );
  }, [] );

  const loadContractTables = useCallback( async( contractId: string, nodeId: string ) => {
    if ( loadingNodesRef.current.has( nodeId ) ) return;
    loadingNodesRef.current.add( nodeId );
    setTreeNodes( prev => updateNode( prev, nodeId, n => ( { ...n, isLoading: true } ) ) );

    try {
      const contract = await APIGateway.dataContracts.getContract( contractId );
      const parsed = parseDataContractYAML( contract.yaml_content );
      const models = parsed?.models || {};

      const tableNodes: TreeNode[] = Object.entries( models ).map( ( [ tableName, model ] ) => {
        const fields = model.fields || {};
        const columnNodes: TreeNode[] = Object.entries( fields ).map( ( [ fieldName, field ] ) => ( {
          id: `col_${ contractId }_${ tableName }_${ fieldName }`,
          label: `${ fieldName } (${ field.type })`,
          type: 'column' as const,
          metadata: {
            contractId,
            tableName,
            columnType: field.type
          }
        } ) );

        return {
          id: `table_${ contractId }_${ tableName }`,
          label: tableName,
          type: 'table' as const,
          isExpanded: false,
          metadata: { contractId, tableName },
          children: columnNodes
        };
      } );

      setTreeNodes( prev => updateNode( prev, nodeId, n => ( {
        ...n,
        isLoading: false,
        isExpanded: true,
        children: tableNodes.length > 0 ? tableNodes : [ {
          id: `empty_${ contractId }`,
          label: 'No tables defined',
          type: 'column' as const
        } ]
      } ) ) );
    } catch( e ) {
      console.error( 'Failed to load tables:', e );
      setTreeNodes( prev => updateNode( prev, nodeId, n => ( {
        ...n,
        isLoading: false,
        isExpanded: true,
        children: [ {
          id: `error_${ contractId }`,
          label: 'Failed to load tables',
          type: 'column' as const
        } ]
      } ) ) );
    } finally {
      loadingNodesRef.current.delete( nodeId );
    }
  }, [ updateNode ] );

  const handleToggle = useCallback( ( nodeId: string ) => {
    // Find the node
    const findNode = ( nodes: TreeNode[] ): TreeNode | undefined => {
      for ( const n of nodes ) {
        if ( n.id === nodeId ) return n;
        if ( n.children ) {
          const found = findNode( n.children );
          if ( found ) return found;
        }
      }
      return undefined;
    };

    const node = findNode( treeNodes );
    if ( !node ) return;

    if ( node.type === 'contract' && !node.isExpanded && ( !node.children || node.children.length === 0 ) ) {
      // Lazy load tables
      loadContractTables( node.metadata!.contractId!, nodeId );
    } else {
      setTreeNodes( prev => updateNode( prev, nodeId, n => ( { ...n, isExpanded: !n.isExpanded } ) ) );
    }
  }, [ treeNodes, updateNode, loadContractTables ] );

  if ( isLoading ) {
    return (
      <div className={ styles.explorerLoading }>
        <Icon icon={ AiOutlineLoading } className={ styles.treeSpinner } />
        <span>Loading contracts...</span>
      </div>
    );
  }

  return (
    <div className={ styles.databaseExplorer } data-testid="database-explorer">
      <div className={ styles.explorerHeader }>
        <Heading as="h3" size="sm">Connected Data Sources</Heading>
      </div>
      <div className={ styles.explorerTree }>
        { treeNodes.map( node => (
          <TreeNodeItem
            key={ node.id }
            node={ node }
            depth={ 0 }
            onToggle={ handleToggle }
            onTableDoubleClick={ onTableDoubleClick }
          />
        ) ) }
        { treeNodes.length === 0 && (
          <div className={ styles.emptyState }>No contracts found</div>
        ) }
      </div>
    </div>
  );
}

export default memo( DatabaseExplorer );
